import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import { MessageCircle } from 'lucide-react';
import type { SpeakingMetricsDoc } from '../../../tutor/types/tutorAnalytics';

interface TutorCommunicationRubricProps {
  speakingSessions: SpeakingMetricsDoc[];
}

const RUBRIC_KEYS = ['initiative', 'responsiveness', 'engagement', 'tamil_use'] as const;

const LABELS: Record<(typeof RUBRIC_KEYS)[number], string> = {
  initiative: 'Initiative',
  responsiveness: 'Responsiveness',
  engagement: 'Engagement',
  tamil_use: 'Tamil use',
};

function isCommunicationSession(s: SpeakingMetricsDoc): boolean {
  const tt = (s.tutor_type || '').toUpperCase();
  if (tt === 'TOPIC') return true;
  const r = s.rubric_scores;
  return !!(r && RUBRIC_KEYS.some((k) => typeof r[k] === 'number'));
}

function formatTopicLabel(label: string | null | undefined): string {
  const t = String(label ?? '').trim();
  return t || '—';
}

function formatSessionWhen(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export const TutorCommunicationRubric: React.FC<TutorCommunicationRubricProps> = ({
  speakingSessions,
}) => {
  const topicSessions = useMemo(
    () => speakingSessions.filter(isCommunicationSession).slice(0, 5),
    [speakingSessions],
  );

  if (topicSessions.length === 0) {
    return null;
  }

  const latest = topicSessions[0];
  const rubric = latest.rubric_scores;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-app-text dark:text-white flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />
          Communication practice (rubric)
        </CardTitle>
        <p className="text-sm text-app-text-secondary dark:text-gray-400 mt-1">
          Scores reflect your last conversation-style sessions; they are not shown during live class.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {rubric ? (
          <div>
            <div className="text-sm font-medium text-app-text-secondary dark:text-gray-400 mb-1">
              Latest session — {formatSessionWhen(latest.created_at)}
            </div>
            <div className="text-sm text-app-text dark:text-gray-100 font-semibold mb-3">
              Topic: {formatTopicLabel(latest.topic_label)}
            </div>
            <div className="space-y-3">
              {RUBRIC_KEYS.map((key) => {
                const value = Math.min(100, Math.max(0, Number(rubric[key] ?? 0)));
                return (
                  <div key={key}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-app-text dark:text-gray-200">{LABELS[key]}</span>
                      <span className="text-app-text-secondary dark:text-gray-400 tabular-nums">
                        {value}%
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-emerald-500/90 dark:bg-emerald-400/90 transition-[width]"
                        style={{ width: `${value}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-sm text-app-text-secondary dark:text-gray-400">
              <span>
                Overall:{' '}
                <span className="font-semibold text-app-text dark:text-white">
                  {latest.scores.overall}%
                </span>
              </span>
              <span>Level: {latest.speaking_level || '—'}</span>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-sm text-app-text dark:text-gray-100 font-semibold">
              Topic: {formatTopicLabel(latest.topic_label)}
            </div>
            <p className="text-sm text-app-text-secondary dark:text-gray-400">
              Rubric breakdown is not stored for this session yet. Complete a new communication session to
              see initiative, responsiveness, engagement, and Tamil use scores here.
            </p>
          </div>
        )}

        {topicSessions.length > 1 && (
          <div>
            <div className="text-sm font-medium text-app-text-secondary dark:text-gray-400 mb-2">
              Recent sessions (overall)
            </div>
            <ul className="space-y-2 text-sm">
              {topicSessions.map((s) => (
                <li
                  key={s.session_id}
                  className="flex justify-between gap-4 border-b border-gray-100 dark:border-gray-800 pb-2 last:border-0"
                >
                  <span className="text-app-text dark:text-gray-200 min-w-0">
                    <span className="block truncate">{formatSessionWhen(s.created_at)}</span>
                    <span className="block text-xs text-app-text-secondary dark:text-gray-500 truncate">
                      Topic: {formatTopicLabel(s.topic_label)}
                    </span>
                  </span>
                  <span className="tabular-nums text-app-text-secondary dark:text-gray-400 shrink-0 self-center">
                    {s.scores.overall}%
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
